import { StandardEvents } from '@shopify/events';

/* sis-price (DS PDP price row): the row renders in Liquid from the selected
   variant; on variant change it re-renders per convention 11b - the stock
   product-information section is NOT wholesale-morphed, each block listens for
   StandardEvents.productSelect and swaps its own subtree by data-block-id from
   the detail.html the event promise resolves with. Idiom copied from
   assets/sis-buy-summary.js.

   Instant repaint (owner 2026-09-24, same change as the order summary): a
   size/colour click repaints the price, compare-at and Save pill at once from
   the 'sis:variant-preview' event sis-option-tiles dispatches with the matched
   variant, instead of waiting for the section request. The server swap still
   follows and has the last word. Money goes through window.SisMoney
   (assets/sis-money.js), which reproduces Liquid's money_without_trailing_zeros
   for this request - store format, decimals rule and the ht-money wrapper the
   Liquid row carries. */
class SisPrice extends HTMLElement {
  #section;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, [id^="shopify-section"]') || document;
    this.#section.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#section.addEventListener('sis:variant-preview', this.#onPreview);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#section?.removeEventListener('sis:variant-preview', this.#onPreview);
  }

  #onProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        const next = detail?.html?.querySelector(`sis-price[data-block-id="${this.dataset.blockId}"]`);
        if (next) this.innerHTML = next.innerHTML; // fresh prices for the new variant
      })
      .catch(() => {});
  };

  #onPreview = (event) => {
    const d = event.detail || {};
    if (String(d.productId) !== String(this.dataset.productId)) return;
    this.#paint(d.price / 100, (d.compare || 0) / 100);
  };

  // same structure as the Liquid row, built with textContent only
  #paint(price, compare) {
    const onSale = compare > price;
    const hidden = (text) => {
      const s = document.createElement('span');
      s.className = 'visually-hidden';
      s.textContent = text;
      return s;
    };
    const now = document.createElement('span');
    now.className = 'sis-price__now';
    now.append(hidden((onSale ? this.dataset.labelSale : this.dataset.labelRegular) || ''), this.#moneyNode(price));
    const parts = [now];
    if (onSale) {
      const s = document.createElement('s');
      s.className = 'sis-price__compare';
      s.append(hidden(this.dataset.labelRegular || ''), this.#moneyNode(compare));
      parts.push(s);
      if (this.dataset.saveTemplate) {
        const pill = document.createElement('span');
        pill.className = 'sis-price__save';
        pill.textContent = this.dataset.saveTemplate.replace('__SIS_AMOUNT__', this.#moneyText(compare - price));
        parts.push(pill);
      }
    }
    this.replaceChildren(...parts);
  }

  // the store's money markup (wrapper included), as Liquid renders it
  #moneyNode(n) {
    const tpl = document.createElement('template');
    tpl.innerHTML = window.SisMoney ? window.SisMoney.html(n) : this.#moneyText(n);
    return tpl.content;
  }

  #moneyText(n) {
    if (window.SisMoney) return window.SisMoney.text(n);
    return '$' + n.toFixed(2).replace(/\.00$/, '');
  }
}

if (!customElements.get('sis-price')) {
  customElements.define('sis-price', SisPrice);
}
